import React, { DragEvent, ReactNode, forwardRef, memo, useLayoutEffect, useRef, useState } from 'react';
import Draggable, { DraggableData, DraggableEvent } from 'react-draggable';
import { useEditorStore } from './editor-store';
import classNames from 'classnames';
import { BlockContent } from './types';
import { generateBlockId } from './helpers/generate-block-id';
import { useFloating, useDelayGroup, useId } from '@floating-ui/react';
import { offset, autoUpdate } from '@floating-ui/dom';
import { useRefCallback } from 'src/utils/react/ref-callback.hook';
import { useRequestAnimationFrame } from './utils/request-animation-frame.hook';
import { NeueTooltip } from './components/tooltip';
import { trackSegmentEvent } from 'src/utils/segment';
import { shallow } from 'zustand/shallow';
import { useEditorDragActions } from './utils/editor-drag-actions.hook';

const CLICK_DELTA_THRESHOLD = 10;

type Props = {
  children: ReactNode | ((dragging: boolean) => ReactNode);
  blockContent: BlockContent;
  noop?: boolean;
  tooltipClassName?: string;
  joyrideClassname?: string;
  tooltip?: string;
  disabled?: boolean;
  onDragStart?: () => void;
  onDrag?: (node: ReactNode, exceedThreshold: boolean) => void;
  onDrop?: () => void;
  onMouseDown?: () => void;
  disablePointerEvents?: boolean;
  defaultDraggingClassName?: string;
  defaultClassName?: string;
  buttonClassName?: string;
  relativePositioning?: boolean;
};

export const AddButtonComponent = memo(
  forwardRef<
    HTMLDivElement,
    { children: ReactNode; dragging: boolean; dragVersion: boolean; disablePointerEvents?: boolean; className?: string; disabled?: boolean }
  >(({ children, dragVersion, dragging, className, disablePointerEvents = false, disabled = false }, ref) => (
    <div
      ref={ref}
      className={classNames(
        'cc-drag-handle rounded transition-colors text-neue-journey-fg',
        {
          'group-hover:bg-neue-journey-bg-90': !dragVersion && !dragging,
          'cursor-grabbing bg-neue-journey-bg': dragVersion && dragging,
          'cursor-pointer bg-transparent': dragVersion && !dragging,
          'opacity-40 pointer-events-none cursor-not-allowed': disabled,
        },
        className || {
          'p-1.5': !dragVersion,
          'p-2.5': dragVersion,
          'pointer-events-auto': !disablePointerEvents,
          'pointer-events-none': disablePointerEvents,
        }
      )}
    >
      {children}
    </div>
  ))
);

export const ContentCreationAddBlockButton = memo(
  ({
    children,
    blockContent,
    noop = false,
    disabled = false,
    tooltip,
    tooltipClassName = 'z-neue-new-block-button-tooltip',
    joyrideClassname='',
    onDrag,
    onDrop,
    onMouseDown,
    disablePointerEvents,
    defaultDraggingClassName = 'w-10 h-10 transition-opacity opacity-100',
    relativePositioning = true,
    buttonClassName,
    defaultClassName = 'absolute opacity-0 inset-0',
  }: Props) => {
    const currentSectionId = useEditorStore((state) => state.currentSectionId, shallow);
    const editorContentRect = useEditorStore((state) => state.editorContentRect, shallow);
    const outerAreaRect = useEditorStore((state) => state.outerAreaRect, shallow);
    const innerAreaRect = useEditorStore((state) => state.innerAreaRect, shallow);
    const dispatchUserEditorAction = useEditorStore((state) => state.dispatchUserEditorAction);
    const setIsDraggingSomethingOnEditor = useEditorStore((state) => state.setIsDraggingSomethingOnEditor);
    const selectBlock = useEditorStore((state) => state.selectBlock);
    const clearSelectedBlock = useEditorStore((state) => state.clearSelectedBlock);
    const setScrollToBlockId = useEditorStore((state) => state.setScrollToBlockId);

    const [dragging, setDragging] = useState(false);
    const [position, setPosition] = useState({ x: 0, y: 0 });

    const { onDrag: onBlockDrag, onDrop: onBlockDragStop, resetBoundaries } = useEditorDragActions();
    const draggingActiveRef = useRef(false);

    const [isOpen, setIsOpen] = useState(false);
    const { refs, context, update } = useFloating({
      middleware: [offset(16)],
      placement: 'left',
      strategy: 'fixed',
      open: isOpen,
      onOpenChange: setIsOpen,
    });
    const id = useId();

    useDelayGroup(context, { id });

    useLayoutEffect(() => {
      if (isOpen && refs.reference.current && refs.floating.current) {
        // const refRect = refs.reference.current.getBoundingClientRect();
        // const floatingRect = refs.floating.current.getBoundingClientRect();
        // // console.log('autoUpdate', JSON.stringify(refRect), JSON.stringify(floatingRect));
        return autoUpdate(refs.reference.current, refs.floating.current, update);
      }
    }, [isOpen, update, refs.floating, refs.reference]);

    const runOnRaf = useRequestAnimationFrame();

    const onStart = (e: DraggableEvent, args: DraggableData) => {
      draggingActiveRef.current = true;
      setIsDraggingSomethingOnEditor(true);
    };

    const extendBlockContent = (blockContent: BlockContent) => {
      if (blockContent.type === 'mutual-action-plan') {
        return {
          ...blockContent,
          uuid: generateBlockId(),
        };
      }

      return blockContent;
    };

    const onBlockButtonDrag = useRefCallback((e: DraggableEvent, args: DraggableData) => {
      const { x, y } = args;
      const { pageX, pageY } = e as MouseEvent;
      runOnRaf(() => {
        if (draggingActiveRef.current) {
          clearSelectedBlock();
          onBlockDrag(e as DragEvent<HTMLDivElement>, args.node);
          setPosition(relativePositioning ? { x, y } : { x: pageX, y: pageY });
          setDragging(true);
          if (onDrag) {
            onDrag(DraggableElement, Math.abs(x) > CLICK_DELTA_THRESHOLD || Math.abs(y) > CLICK_DELTA_THRESHOLD);
          }
        }
      });
    });

    // const throttledOnDrag = useMemo(
    //   () => throttle((e: DraggableEvent, args: DraggableData) => onDrag(e, args), 17),
    //   [onDrag]
    // );

    const onStop = (e: DraggableEvent, args: DraggableData) => {
      draggingActiveRef.current = false;
      const { x, y } = args;
      const performanceStart = performance.now();
      if (dragging && (Math.abs(x) > CLICK_DELTA_THRESHOLD || Math.abs(y) > CLICK_DELTA_THRESHOLD)) {
        if (noop) {
          setDragging(false);
          setPosition({ x: 0, y: 0 });
          useEditorStore.setState({
            insertionBoundary: null,
            appendingSection: false,
          });
        } else {
          console.log('onStop', 'before endDragging', performance.now() - performanceStart);
          if (editorContentRect && outerAreaRect && innerAreaRect) {
            const generatedBlockId = onBlockDragStop(
              e as DragEvent<HTMLDivElement>,
              extendBlockContent(blockContent),
              args.node
            );
            setTimeout(() => {
              if (generatedBlockId) {
                selectBlock(generatedBlockId);
              }
            }, 0);
          }
          setDragging(false);
          setPosition({ x: 0, y: 0 });
          resetBoundaries();
          // endDragging({ draggableId, blockContent, dragElementRect: rect, mousePosition: { clientX, clientY } });
          console.log('onStop', 'after endDragging', performance.now() - performanceStart);
        }
      } else if (!noop) {
        const id = generateBlockId();
        dispatchUserEditorAction({
          type: 'append-block',
          content: extendBlockContent(blockContent),
          sectionId: currentSectionId || undefined,
          layoutExtraParams: {},
          id,
        });
        trackSegmentEvent('Editor Block Added', {
          type: blockContent.type,
          dragged: false,
        });
        setDragging(false);
        setPosition({ x: 0, y: 0 });
        useEditorStore.setState({
          isDraggingNewBlock: false,
          insertionBoundary: null,
          appendingSection: false,
        });
        selectBlock(id);
        setScrollToBlockId(id);
      } else {
        setDragging(false);
        setPosition({ x: 0, y: 0 });
        useEditorStore.setState({
          isDraggingNewBlock: false,
          insertionBoundary: null,
          appendingSection: false,
        });
      }
      if (onDrop) {
        onDrop();
      }
      setIsDraggingSomethingOnEditor(false);
      console.log('onStop', performance.now() - performanceStart);
    };

    const DraggableElement = (
      <Draggable
        disabled={disabled}
        defaultClassName={defaultClassName}
        defaultClassNameDragging={classNames('cc-draggable-dragging -left-1 -top-1', defaultDraggingClassName)}
        position={dragging ? position : { x: 0, y: 0 }}
        onStart={onStart}
        onStop={onStop}
        onDrag={onBlockButtonDrag}
        onMouseDown={(e) => {
          if (!disabled) {
            if (!relativePositioning) {
              if (onMouseDown) {
                onMouseDown();
              }
              setIsDraggingSomethingOnEditor(true);
              setPosition({ x: e.pageX, y: e.pageY });
              setDragging(true);
          }
          }
        }}
      >
        <div>
          <AddButtonComponent
            className={buttonClassName}
            disablePointerEvents={disablePointerEvents}
            dragVersion
            dragging={dragging}
            disabled={disabled}
          >
            {typeof children === 'function' ? children(dragging) : children}
          </AddButtonComponent>
        </div>
      </Draggable>
    );

    const ContentElement = (
      <div className={classNames('group relative z-neue-new-block-button-dragging',joyrideClassname?joyrideClassname:'')}>
        <AddButtonComponent className={buttonClassName} disablePointerEvents dragVersion={false} dragging={dragging} disabled={disabled}>
          {typeof children === 'function' ? children(false) : children}
        </AddButtonComponent>
        {DraggableElement}
      </div>
    );

    if (tooltip) {
      return (
        // offset is 8 (default offset from source) + 8 (padding within the new block buttons bar) = 16
        <NeueTooltip
          className={tooltipClassName}
          offset={16}
          placement='left'
          tooltipContent={tooltip}
          forceHide={dragging}
        >
          {ContentElement}
        </NeueTooltip>
      );
    }

    return ContentElement;
  }
);
